import React, { useState } from "react";
import { Box, Typography, Modal, IconButton, Chip } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE_URL } from "../../utils/api";

const fileIconFor = (fileType) => {
  if (fileType === "application/pdf") return <PictureAsPdfIcon sx={{ fontSize: 18 }} />;
  if (fileType?.startsWith("image/")) return <ImageIcon sx={{ fontSize: 18 }} />;
  return <InsertDriveFileIcon sx={{ fontSize: 18 }} />;
};

const modalPaperSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(900px, 92vw)",
  maxHeight: "88vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  display: "flex",
  flexDirection: "column",
};

// Read-only election documents panel shown on the ballot page itself — voters
// never leave the ballot or open a separate window/tab to review attachments.
const ElectionDocumentsViewer = ({ documents }) => {
  const [previewDoc, setPreviewDoc] = useState(null);

  if (!documents?.length) return null;

  const fileUrl = (doc) => `${API_BASE_URL}/election-documents/file/${doc.id}`;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>
        Election Documents
      </Typography>
      {documents.some((d) => d.file_type?.startsWith("image/")) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
          {documents
            .filter((d) => d.file_type?.startsWith("image/"))
            .map((doc) => (
              <Box
                key={doc.id}
                component="img"
                src={fileUrl(doc)}
                alt={doc.file_name}
                title={`${doc.file_name} — click to enlarge`}
                onClick={() => setPreviewDoc(doc)}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 220,
                  borderRadius: "10px",
                  border: "1px solid rgba(47,99,173,0.12)",
                  cursor: "zoom-in",
                  objectFit: "contain",
                  backgroundColor: "#fff",
                }}
              />
            ))}
        </Box>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {documents.map((doc) => (
          <Chip
            key={doc.id}
            icon={fileIconFor(doc.file_type)}
            label={doc.file_name}
            onClick={() => setPreviewDoc(doc)}
            clickable
            sx={{
              borderRadius: "8px",
              backgroundColor: "rgba(47,99,173,0.06)",
              color: "#223142",
              fontWeight: 600,
              "&:hover": { backgroundColor: "rgba(47,99,173,0.12)" },
            }}
          />
        ))}
      </Box>

      <Modal open={!!previewDoc} onClose={() => setPreviewDoc(null)}>
        <Box sx={modalPaperSx}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", minWidth: 0, wordBreak: "break-word" }}>
              {previewDoc?.file_name}
            </Typography>
            <IconButton onClick={() => setPreviewDoc(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
            {previewDoc?.file_type === "application/pdf" ? (
              <iframe
                src={fileUrl(previewDoc)}
                title={previewDoc.file_name}
                width="100%"
                height="600px"
                style={{ border: "1px solid rgba(47,99,173,0.08)", borderRadius: "12px", background: "#fff" }}
              />
            ) : previewDoc?.file_type?.startsWith("image/") ? (
              <Box component="img" src={fileUrl(previewDoc)} alt={previewDoc.file_name} sx={{ maxWidth: "100%", borderRadius: "12px" }} />
            ) : (
              <Box>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  This file type can't be previewed here.
                </Typography>
                <a href={previewDoc ? fileUrl(previewDoc) : "#"} target="_blank" rel="noopener noreferrer">
                  Open the file
                </a>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ElectionDocumentsViewer;
